import axios from 'axios';
import { useErrorStore } from './stores/errorStore';
import { resolveApiBaseUrl } from './config/apiBase.js';
import { sessionState, markSessionExpired, checkSessionAfterEmpty404 } from './session/sessionExpiry.js';

// Dev: same-origin so Vite `server.proxy` forwards `/api`.
// Prod: derive API host from the page URL (console.ocdevN → api.v2.test.ocdevN).
const backendUrl = resolveApiBaseUrl();
axios.defaults.baseURL = backendUrl;

// Disable credentials (cookies) since we're using Bearer tokens
axios.defaults.withCredentials = false;

// Set default headers
axios.defaults.headers.common['Accept'] = 'application/json';
axios.defaults.headers.common['Content-Type'] = 'application/json';

// Track retry attempts per request
const retryAttempts = new Map();

// Helper function to show error in overlay
const showErrorInOverlay = (error) => {
    // Use nextTick and setTimeout to ensure Pinia store is available
    // This handles the case where axios is imported before Pinia is initialized
    const tryShowError = () => {
        try {
            // Check if we're in browser environment
            if (typeof window === 'undefined') return;
            
            const errorStore = useErrorStore();
            
            // Determine error type
            let errorType = 'unknown';
            if (error.response?.data?.exception === 'ParseError' || 
                error.response?.data?.exception === 'SyntaxError' ||
                error.message?.includes('parse') || 
                error.message?.includes('syntax') ||
                error.message?.includes('ParseError')) {
                errorType = 'parse';
            } else if (!error.response) {
                errorType = 'network';
            } else if (error.response.status >= 500) {
                errorType = 'server';
            }
            
            // Capture request payload/data
            let requestPayload = null;
            let requestHeaders = null;
            
            if (error.config) {
                // Capture request data/payload
                if (error.config.data) {
                    try {
                        // Handle FormData
                        if (error.config.data instanceof FormData) {
                            const formDataObj = {};
                            error.config.data.forEach((value, key) => {
                                formDataObj[key] = value;
                            });
                            requestPayload = formDataObj;
                        }
                        // Handle URLSearchParams
                        else if (error.config.data instanceof URLSearchParams) {
                            requestPayload = Object.fromEntries(error.config.data);
                        }
                        // Handle string (try to parse as JSON)
                        else if (typeof error.config.data === 'string') {
                            try {
                                requestPayload = JSON.parse(error.config.data);
                            } catch (e) {
                                // If parsing fails, store as string
                                requestPayload = error.config.data;
                            }
                        }
                        // Handle objects/arrays
                        else if (typeof error.config.data === 'object') {
                            requestPayload = error.config.data;
                        }
                        // Fallback for other types
                        else {
                            requestPayload = error.config.data;
                        }
                    } catch (e) {
                        // If anything fails, store as string representation
                        requestPayload = String(error.config.data);
                    }
                } else if (error.config.params) {
                    // For GET requests, params might be in config.params
                    requestPayload = error.config.params;
                }
                
                // Capture request headers (keep all headers, including Authorization)
                if (error.config.headers) {
                    requestHeaders = { ...error.config.headers };
                    // Note: We keep Authorization token visible but will hide it in UI with toggle
                }
            }
            
            // Capture response headers
            let responseHeaders = null;
            let isTestEvent = false;
            
            if (error.response?.headers) {
                const headers = error.response.headers;
                responseHeaders = {};
                
                // Convert headers to plain object for display
                // Axios can store headers in different formats
                try {
                    // Method 1: Headers object with forEach (Axios Headers object)
                    if (headers.forEach && typeof headers.forEach === 'function') {
                        headers.forEach((value, key) => {
                            // Handle array values (some headers can have multiple values)
                            responseHeaders[key] = Array.isArray(value) ? value.join(', ') : value;
                        });
                    }
                    // Method 2: Plain object
                    else if (typeof headers === 'object' && !headers.get) {
                        for (const [key, value] of Object.entries(headers)) {
                            // Skip internal axios properties
                            if (!key.startsWith('_')) {
                                responseHeaders[key] = Array.isArray(value) ? value.join(', ') : value;
                            }
                        }
                    }
                    // Method 3: Headers with get method
                    else if (headers.get && typeof headers.get === 'function') {
                        // Try to get all headers by iterating
                        if (headers.keys && typeof headers.keys === 'function') {
                            for (const key of headers.keys()) {
                                const value = headers.get(key);
                                responseHeaders[key] = Array.isArray(value) ? value.join(', ') : value;
                            }
                        } else {
                            // Fallback: try common header names
                            const commonHeaders = [
                                'content-type', 'content-length', 'date', 'server',
                                'oc_test_env', 'oc-test-env', 'Oc_test_env',
                                'access-control-allow-origin', 'cache-control'
                            ];
                            for (const headerName of commonHeaders) {
                                const value = headers.get(headerName);
                                if (value) {
                                    responseHeaders[headerName] = Array.isArray(value) ? value.join(', ') : value;
                                }
                            }
                        }
                    }
                    
                    // Method 4: Check for _headers property (internal axios storage)
                    if (headers._headers && Object.keys(responseHeaders).length === 0) {
                        for (const [key, value] of Object.entries(headers._headers)) {
                            const headerValue = Array.isArray(value) ? value[0] : value;
                            responseHeaders[key] = headerValue;
                        }
                    }
                    
                    // If still empty, try to copy all enumerable properties
                    if (Object.keys(responseHeaders).length === 0) {
                        for (const key in headers) {
                            if (headers.hasOwnProperty(key) && !key.startsWith('_')) {
                                const value = headers[key];
                                if (value !== null && value !== undefined) {
                                    responseHeaders[key] = Array.isArray(value) ? value.join(', ') : String(value);
                                }
                            }
                        }
                    }
                } catch (e) {
                    console.warn('Failed to capture response headers:', e);
                    // Fallback: try to get at least some headers
                    if (error.response.headers && typeof error.response.headers === 'object') {
                        responseHeaders = { ...error.response.headers };
                    }
                }
                
                // Check if this is a test event (check for oc_test_env header)
                // Try multiple methods to access the header (case-insensitive)
                
                // Method 1: Check in normalized responseHeaders object
                const headerKeys = Object.keys(responseHeaders || {});
                const testEnvKey = headerKeys.find(key => {
                    const lowerKey = key.toLowerCase();
                    return lowerKey === 'oc_test_env' || 
                           lowerKey === 'oc-test-env' ||
                           lowerKey === 'oc_test_env' ||
                           lowerKey.replace(/-/g, '_') === 'oc_test_env' ||
                           lowerKey.replace(/_/g, '-') === 'oc-test-env';
                });
                
                if (testEnvKey) {
                    const testEnvValue = responseHeaders[testEnvKey];
                    isTestEvent = testEnvValue === 'true' || testEnvValue === true || testEnvValue === 'True';
                }
                
                // Method 2: Direct access with different case variations (if Method 1 didn't find it)
                if (!isTestEvent && headers) {
                    // Try direct property access with different cases
                    const possibleKeys = [
                        'oc_test_env', 'oc-test-env', 'Oc_test_env', 'Oc-Test-Env',
                        'OC_TEST_ENV', 'OC-TEST-ENV', 'oc_test_env', 'oc_test_env'
                    ];
                    
                    for (const key of possibleKeys) {
                        const value = headers[key] || 
                                     headers.get?.(key) ||
                                     headers.get?.(key.toLowerCase());
                        if (value === 'true' || value === true || value === 'True') {
                            isTestEvent = true;
                            break;
                        }
                    }
                }
                
                // Method 3: Check raw headers if available (axios sometimes stores in _headers)
                if (!isTestEvent && headers._headers) {
                    for (const [key, value] of Object.entries(headers._headers)) {
                        if (key.toLowerCase().replace(/-/g, '_') === 'oc_test_env') {
                            const headerValue = Array.isArray(value) ? value[0] : value;
                            if (headerValue === 'true' || headerValue === true || headerValue === 'True') {
                                isTestEvent = true;
                                break;
                            }
                        }
                    }
                }
                
                // Method 4: Iterate through all headers and check case-insensitively
                if (!isTestEvent) {
                    const checkHeader = (headerObj) => {
                        if (!headerObj) return false;
                        
                        // Try forEach if available
                        if (headerObj.forEach && typeof headerObj.forEach === 'function') {
                            let found = false;
                            headerObj.forEach((value, key) => {
                                const lowerKey = String(key).toLowerCase().replace(/-/g, '_');
                                if (lowerKey === 'oc_test_env') {
                                    const headerValue = Array.isArray(value) ? value[0] : value;
                                    if (headerValue === 'true' || headerValue === true || String(headerValue).toLowerCase() === 'true') {
                                        found = true;
                                    }
                                }
                            });
                            return found;
                        }
                        
                        // Try Object.entries
                        for (const [key, value] of Object.entries(headerObj)) {
                            const lowerKey = String(key).toLowerCase().replace(/-/g, '_');
                            if (lowerKey === 'oc_test_env') {
                                const headerValue = Array.isArray(value) ? value[0] : value;
                                if (headerValue === 'true' || headerValue === true || String(headerValue).toLowerCase() === 'true') {
                                    return true;
                                }
                            }
                        }
                        return false;
                    };
                    
                    isTestEvent = checkHeader(headers) || checkHeader(responseHeaders);
                }
            }
            
            // Final fallback: if responseHeaders is still null/empty, try one more time
            if ((!responseHeaders || Object.keys(responseHeaders).length === 0) && error.response?.headers) {
                try {
                    // Try JSON.stringify and parse to get all properties
                    const headersStr = JSON.stringify(error.response.headers);
                    if (headersStr && headersStr !== '{}') {
                        const parsed = JSON.parse(headersStr);
                        if (parsed && typeof parsed === 'object') {
                            responseHeaders = {};
                            for (const [key, value] of Object.entries(parsed)) {
                                if (!key.startsWith('_')) {
                                    responseHeaders[key] = Array.isArray(value) ? value.join(', ') : String(value);
                                }
                            }
                        }
                    }
                } catch (e) {
                    // Last resort: create a minimal headers object
                    responseHeaders = { 
                        note: 'Headers could not be fully captured',
                        raw: String(error.response.headers)
                    };
                }
            }
            
            // Build error data
            const errorData = {
                type: errorType,
                message: error.response?.data?.message || error.message || 'An unknown error occurred',
                exception: error.response?.data?.exception || null,
                file: error.response?.data?.file || null,
                line: error.response?.data?.line || null,
                trace: error.response?.data?.trace || null,
                responseData: error.response?.data || null,
                responseHeaders: responseHeaders,
                requestPayload: requestPayload,
                requestHeaders: requestHeaders,
                url: error.config?.url || error.request?.responseURL || null,
                method: error.config?.method?.toUpperCase() || null,
                status: error.response?.status || null,
                statusText: error.response?.statusText || null,
                isTestEvent: isTestEvent || false
            };
            
            errorStore.setError(errorData);
        } catch (storeError) {
            // If store is not available yet, try again after a short delay
            if (storeError.message?.includes('getActivePinia') || 
                storeError.message?.includes('Pinia')) {
                setTimeout(tryShowError, 100);
            } else {
                console.error('Failed to show error in overlay:', storeError);
            }
        }
    };
    
    // Try immediately, and if it fails, retry after a delay
    setTimeout(tryShowError, 0);
};

// Add request interceptor to manage Bearer token authentication
axios.interceptors.request.use(async config => {
    // Add Authorization header if token exists
    const token = localStorage.getItem('auth_token');
    if (token && !token.startsWith('demo_token')) {
        config.headers['Authorization'] = `Bearer ${token}`;
    }

    // Always set X-Requested-With for AJAX requests
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    
    // No CSRF token needed for Bearer authentication
    return config;
});

// Add response interceptor to handle errors
axios.interceptors.response.use(
    response => {
        // Clear retry count on successful requests
        if (response.config.url) {
            retryAttempts.delete(response.config.url);
        }
        return response;
    },
    async error => {
        const config = error.config;
        const isLoginRequest = /\/?api\/login$/.test(config?.url || '');

        // Expired/revoked token: the API answers api/* with an empty 404. Confirm with a
        // probe of /api/user and show the re-login popup instead of silently empty panels.
        if (!isLoginRequest && error.response?.status === 404 &&
            (error.response.data === '' || error.response.data == null)) {
            checkSessionAfterEmpty404();
        }

        // Wrong password typed into the re-login popup: let the popup show the error
        if (isLoginRequest && sessionState.expired) {
            return Promise.reject(error);
        }

        // 401 while logged in: same popup, no hard redirect (keeps the page and its state)
        if (!isLoginRequest && error.response?.status === 401 && localStorage.getItem('auth_token')) {
            markSessionExpired();
            if (sessionState.expired) {
                return Promise.reject(error);
            }
        }

        // Handle 401 Unauthorized errors
        if (error.response?.status === 401) {
            // Clear invalid token and redirect to login
            localStorage.removeItem('auth_token');
            localStorage.removeItem('userRole');
            delete axios.defaults.headers.common['Authorization'];
            
            // If we're not already on login page, redirect
            if (window.location.pathname !== '/login') {
                console.log('Unauthorized request, redirecting to login');
                window.location.href = '/login';
            }
            return Promise.reject(error);
        }
        
        // Handle token expiration (422 for invalid/expired tokens)
        if (error.response?.status === 422 && error.response?.data?.message?.includes('token')) {
            // Clear expired token and redirect to login
            localStorage.removeItem('auth_token');
            localStorage.removeItem('userRole');
            delete axios.defaults.headers.common['Authorization'];
            
            if (window.location.pathname !== '/login') {
                console.log('Token expired, redirecting to login');
                window.location.href = '/login';
            }
            return Promise.reject(error);
        }
        
        // Show error in overlay for parse errors and other API errors
        // Skip showing overlay for auth errors (401, 422) as they redirect to login
        if (error.response?.status !== 401 && 
            !(error.response?.status === 422 && error.response?.data?.message?.includes('token'))) {
            showErrorInOverlay(error);
        }
        
        return Promise.reject(error);
    }
);

export default axios;